/**
 * The local "recently opened" working set — the console's stand-in for a server list (the results
 * module exposes NO list endpoint: reads are by-id only, the local-binary lookup model). We persist
 * ONLY non-clinical handles (CR id + numero + version + etat + when it was opened) per lab, never any
 * result value (L-4). Scoped by lab id so a lab switch never bleeds another tenant's worklist.
 */
import { useCallback, useEffect, useState } from "react";
import type { CompteRenduEtat } from "./types";

export interface RecentCompteRendu {
  id: string;
  numero: string;
  version: number;
  etat: CompteRenduEtat;
  openedAt: number;
}

const MAX_RECENTS = 12;
const key = (labId: string | null) => `nv.results.recent.${labId ?? "_"}`;

function read(labId: string | null): RecentCompteRendu[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(key(labId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is RecentCompteRendu =>
        !!r && typeof r === "object" && typeof (r as RecentCompteRendu).id === "string",
    );
  } catch {
    return [];
  }
}

function write(labId: string | null, list: RecentCompteRendu[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key(labId), JSON.stringify(list.slice(0, MAX_RECENTS)));
  } catch {
    /* quota / private mode — the recents list is a convenience, never load-bearing */
  }
}

/** Per-lab recents with push/clear. Re-reads when the lab id changes. */
export function useRecentComptesRendus(labId: string | null) {
  const [list, setList] = useState<RecentCompteRendu[]>(() => read(labId));

  useEffect(() => {
    setList(read(labId));
  }, [labId]);

  const remember = useCallback(
    (entry: Omit<RecentCompteRendu, "openedAt">) => {
      setList((prev) => {
        const next = [
          { ...entry, openedAt: Date.now() },
          ...prev.filter((r) => r.id !== entry.id),
        ].slice(0, MAX_RECENTS);
        write(labId, next);
        return next;
      });
    },
    [labId],
  );

  const clear = useCallback(() => {
    setList([]);
    write(labId, []);
  }, [labId]);

  return { recents: list, remember, clear };
}
